import { inject, Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { finalize, switchMap } from "rxjs/operators";

import { AzureBlobService } from "../../../services/azure-blob.service";
import { RecordablesService } from "../../../services/data/recordables.service";
import { SpinnerService } from "../../../services/interceptors/spinner.interceptor";
import { BlobEntityType } from "../../../types/enums/blobentitytype";
import { Recordable } from "../../../types/recordable";
import { DocumentUploadInfo } from "../../../types/documentuploadinfo";
import { DocumentUploadItem } from "../../../types/documentuploaditem";

/**
 * Shared save + attachment upload flow for recordable edit UIs (portal Recordables app, Scheduling sidebar, etc.).
 * Uses one SpinnerService scope for the API save and blob uploads (upload uses plain HTTP + Azure SDK without per-request overlay).
 */
@Injectable({
    providedIn: "root"
})
export class RecordableEditSaveService {
    private recordablesService = inject(RecordablesService);
    private azureBlobService = inject(AzureBlobService);
    private spinnerService = inject(SpinnerService);

    /**
     * POST/PUT the recordable, then upload staged files to blob storage.
     * Starts global overlay via SpinnerService for the entire operation (including Azure upload).
     */
    runSaveWithAttachments(recordable: Recordable, filesToUpload: unknown[]): Observable<Recordable> {
        this.spinnerService.startHttpCall();
        return this.recordablesService.saveRecordable(recordable).pipe(
            switchMap((saved) => this.uploadNewAttachments(saved, filesToUpload)),
            finalize(() => this.spinnerService.finishHttpCall())
        );
    }

    private uploadNewAttachments(recordable: Recordable, filesToUpload: unknown[]): Observable<Recordable> {
        if (!filesToUpload?.length) {
            return of(recordable);
        }

        const uploadItems: DocumentUploadItem[] = filesToUpload.map((file: File) => ({
            file,
            uploadInfo: {
                fileName: file.name,
                data: null,
                entityType: BlobEntityType.Recordable,
                entityId: recordable.id
            } as DocumentUploadInfo<object>
        }));

        return this.azureBlobService.uploadBlob(uploadItems).pipe(switchMap(() => of(recordable)));
    }
}
